import { useState, useCallback } from 'react';
import {
  WizardState,
  MerchantData,
  SubaccountData,
  AgentData,
  FeeGroupAssignment,
  initialWizardState,
  createEmptySubaccount,
  createEmptyAgent,
  createEmptyFeeGroupAssignment,
} from '../types';

export interface UseWizardStateReturn {
  state: WizardState;

  // Step navigation
  currentStep: number;
  setCurrentStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;

  // Merchant
  updateMerchant: (data: Partial<MerchantData>) => void;

  // Subaccounts
  addSubaccount: () => void;
  removeSubaccount: (tempId: string) => void;
  updateSubaccount: (tempId: string, data: Partial<SubaccountData>) => void;

  // Agents
  addAgent: () => void;
  removeAgent: (tempId: string) => void;
  updateAgent: (tempId: string, data: Partial<AgentData>) => void;

  // Agent Fee Group Assignments
  addFeeGroupAssignment: (agentTempId: string) => void;
  removeFeeGroupAssignment: (agentTempId: string, assignmentTempId: string) => void;
  updateFeeGroupAssignment: (agentTempId: string, assignmentTempId: string, data: Partial<FeeGroupAssignment>) => void;

  // Validation
  isStepValid: (step: number) => boolean;
  canProceed: () => boolean;

  // Reset
  resetWizard: () => void;
}

export const useWizardState = (): UseWizardStateReturn => {
  const [state, setState] = useState<WizardState>(initialWizardState);

  // Step navigation
  const setCurrentStep = useCallback((step: number) => {
    setState(prev => ({ ...prev, currentStep: step }));
  }, []);

  const nextStep = useCallback(() => {
    setState(prev => ({ ...prev, currentStep: prev.currentStep + 1 }));
  }, []);

  const prevStep = useCallback(() => {
    setState(prev => ({ ...prev, currentStep: Math.max(0, prev.currentStep - 1) }));
  }, []);

  // Merchant updates
  const updateMerchant = useCallback((data: Partial<MerchantData>) => {
    setState(prev => ({
      ...prev,
      merchant: { ...prev.merchant, ...data },
    }));
  }, []);

  // Subaccount management
  const addSubaccount = useCallback(() => {
    setState(prev => ({
      ...prev,
      subaccounts: [...prev.subaccounts, createEmptySubaccount()],
    }));
  }, []);

  const removeSubaccount = useCallback((tempId: string) => {
    setState(prev => ({
      ...prev,
      subaccounts: prev.subaccounts.filter(s => s.tempId !== tempId),
    }));
  }, []);

  const updateSubaccount = useCallback((tempId: string, data: Partial<SubaccountData>) => {
    setState(prev => ({
      ...prev,
      subaccounts: prev.subaccounts.map(s =>
        s.tempId === tempId ? { ...s, ...data } : s
      ),
    }));
  }, []);

  // Agent management
  const addAgent = useCallback(() => {
    setState(prev => ({
      ...prev,
      agents: [...prev.agents, createEmptyAgent()],
    }));
  }, []);

  const removeAgent = useCallback((tempId: string) => {
    setState(prev => ({
      ...prev,
      agents: prev.agents.filter(a => a.tempId !== tempId),
    }));
  }, []);

  const updateAgent = useCallback((tempId: string, data: Partial<AgentData>) => {
    setState(prev => ({
      ...prev,
      agents: prev.agents.map(a =>
        a.tempId === tempId ? { ...a, ...data } : a
      ),
    }));
  }, []);

  // Fee Group Assignment management (for agents)
  const addFeeGroupAssignment = useCallback((agentTempId: string) => {
    setState(prev => ({
      ...prev,
      agents: prev.agents.map(a =>
        a.tempId === agentTempId
          ? { ...a, feeGroupAssignments: [...a.feeGroupAssignments, createEmptyFeeGroupAssignment()] }
          : a
      ),
    }));
  }, []);

  const removeFeeGroupAssignment = useCallback((agentTempId: string, assignmentTempId: string) => {
    setState(prev => ({
      ...prev,
      agents: prev.agents.map(a =>
        a.tempId === agentTempId
          ? { ...a, feeGroupAssignments: a.feeGroupAssignments.filter(fg => fg.tempId !== assignmentTempId) }
          : a
      ),
    }));
  }, []);

  const updateFeeGroupAssignment = useCallback(
    (agentTempId: string, assignmentTempId: string, data: Partial<FeeGroupAssignment>) => {
      setState(prev => ({
        ...prev,
        agents: prev.agents.map(a =>
          a.tempId === agentTempId
            ? {
                ...a,
                feeGroupAssignments: a.feeGroupAssignments.map(fg =>
                  fg.tempId === assignmentTempId ? { ...fg, ...data } : fg
                ),
              }
            : a
        ),
      }));
    },
    []
  );

  // Validation helpers
  const isValidIPList = (input: string): boolean => {
    if (typeof input !== 'string') return false;
    const trimmed = input.trim();
    if (trimmed === '') return true;

    const parts = trimmed.split(',').map(p => p.trim());
    for (const ip of parts) {
      if (!ip) return false;
      const segments = ip.split('.');
      if (segments.length !== 4) return false;
      for (const seg of segments) {
        if (!/^\d+$/.test(seg)) return false;
        const num = Number(seg);
        if (num < 0 || num > 255) return false;
      }
    }
    return true;
  };

  const isValidUrl = (url: string): boolean => {
    if (!url) return false;
    const urlPattern = /^(https?:\/\/)?([\w.-]+)\.([a-z]{2,6})(\/[\w.-]*)*\/?(\?.*)?(#.*)?$/i;
    return urlPattern.test(url);
  };

  const isValidEmail = (email: string): boolean => {
    if (!email) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isStepValid = useCallback((step: number): boolean => {
    switch (step) {
      case 0: // Merchant step
        return (
          state.merchant.name.trim() !== '' &&
          state.merchant.description.trim() !== '' &&
          isValidIPList(state.merchant.restrictedIps)
        );

      case 1: // Subaccounts or Agents step
        if (state.merchant.type === 'regular') {
          // Regular merchant: validate subaccounts
          if (state.subaccounts.length === 0) return false;
          return state.subaccounts.every(sub => {
            // Required fields from CreateAccount
            if (!sub.name.trim()) return false;
            if (!sub.postbackUrl.trim()) return false;
            if (!isValidUrl(sub.postbackUrl)) return false;
            if (!sub.firstname.trim()) return false;
            if (!sub.lastname.trim()) return false;
            if (!sub.email.trim()) return false;
            if (!isValidEmail(sub.email)) return false;
            if (!sub.businessName.trim()) return false;
            if (!sub.auth1.trim()) return false;
            if (!sub.auth2.trim()) return false;
            // Fee group validation
            if (sub.feeGroup.mode === 'existing' && !sub.feeGroup.existingFeeGroupId) return false;
            if (sub.feeGroup.mode === 'new' && !sub.feeGroup.newFeeGroup?.name) return false;
            return true;
          });
        } else {
          // Agent merchant: validate agents
          if (state.agents.length === 0) return false;
          return state.agents.every(agent => {
            // Required fields from CreateAgent
            if (!agent.firstname.trim()) return false;
            if (!agent.lastname.trim()) return false;
            if (!agent.businessName.trim()) return false;
            if (!agent.email.trim()) return false;
            if (!isValidEmail(agent.email)) return false;
            if (!agent.mobileDialcode.trim()) return false;
            if (!agent.mobile.trim()) return false;
            // Fee group assignments validation
            if (agent.feeGroupAssignments.length === 0) return false;
            return agent.feeGroupAssignments.every(fg => {
              if (!fg.asset || !fg.methodId) return false;
              if (fg.feeGroup.mode === 'existing' && !fg.feeGroup.existingFeeGroupId) return false;
              if (fg.feeGroup.mode === 'new' && !fg.feeGroup.newFeeGroup?.name) return false;
              return true;
            });
          });
        }

      case 2: // Review step
        return true;

      default:
        return false;
    }
  }, [state]);

  const canProceed = useCallback((): boolean => {
    return isStepValid(state.currentStep);
  }, [state.currentStep, isStepValid]);

  // Reset
  const resetWizard = useCallback(() => {
    setState(initialWizardState);
  }, []);

  return {
    state,
    currentStep: state.currentStep,
    setCurrentStep,
    nextStep,
    prevStep,
    updateMerchant,
    addSubaccount,
    removeSubaccount,
    updateSubaccount,
    addAgent,
    removeAgent,
    updateAgent,
    addFeeGroupAssignment,
    removeFeeGroupAssignment,
    updateFeeGroupAssignment,
    isStepValid,
    canProceed,
    resetWizard,
  };
};

export default useWizardState;
